import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Trophy, Plus, ShieldCheck, X, Loader2, Upload, ExternalLink, Trash2,
  ArrowUp, ArrowDown,
} from "lucide-react";

function AchievementCard({ achievement, isOwner, onDelete, onMove, canMoveUp, canMoveDown }) {
  const a = achievement;
  return (
    <div data-testid={`achievement-${a.id}`}
      className="relative group bg-[#0A0A0A] border border-white/5 hover:border-amber-500/30 transition-colors flex flex-col">
      <div className="relative aspect-[4/3] overflow-hidden bg-[#0a0a0a]">
        <img src={a.image_url} alt={a.type} className="w-full h-full object-contain" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A]/90 via-[#0A0A0A]/20 to-transparent" />
        <div className="absolute top-3 right-3 flex items-center gap-1.5 px-2 py-1 bg-black/70 border border-amber-500/40 backdrop-blur-sm">
          <Trophy size={11} className="text-amber-500" />
          <span className="text-xs uppercase tracking-widest font-semibold text-amber-500">{a.type}</span>
        </div>
        {a.verified && (
          <div className="absolute top-3 left-3 flex items-center gap-1.5 px-2 py-1 bg-emerald-500/15 border border-emerald-500/40 backdrop-blur-sm">
            <ShieldCheck size={11} className="text-emerald-400" />
            <span className="text-xs uppercase tracking-widest font-semibold text-emerald-400">Verified</span>
          </div>
        )}
        {/* Owner-only reorder controls (bottom-left) */}
        {isOwner && (
          <div className="absolute bottom-3 left-3 flex items-center gap-1 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
            <button
              type="button"
              onClick={() => onMove(a.id, -1)}
              disabled={!canMoveUp}
              data-testid={`move-up-${a.id}`}
              title="Move earlier"
              className="p-1.5 bg-black/70 border border-white/15 text-neutral-300 hover:text-amber-400 hover:border-amber-500/40 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              <ArrowUp size={12} />
            </button>
            <button
              type="button"
              onClick={() => onMove(a.id, 1)}
              disabled={!canMoveDown}
              data-testid={`move-down-${a.id}`}
              title="Move later"
              className="p-1.5 bg-black/70 border border-white/15 text-neutral-300 hover:text-amber-400 hover:border-amber-500/40 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              <ArrowDown size={12} />
            </button>
          </div>
        )}
      </div>
      <div className="p-4 flex-1 flex flex-col gap-1">
        <div className="text-xs text-neutral-500 uppercase tracking-widest">{a.artist}</div>
        <div className="font-display text-base text-white leading-tight">{a.project}</div>
        <div className="mt-3 pt-3 border-t border-white/5 flex items-center justify-between">
          {a.proof_link ? (
            <a href={a.proof_link} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-xs uppercase tracking-widest font-semibold text-amber-500 hover:underline">
              Proof <ExternalLink size={10} />
            </a>
          ) : <span />}
          {isOwner && (
            <button onClick={() => onDelete(a.id)} data-testid={`delete-achievement-${a.id}`}
              className="text-neutral-600 hover:text-rose-400 transition-colors">
              <Trash2 size={14} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function UploadModal({ open, onClose, onSaved }) {
  const [types, setTypes] = useState([]);
  const [form, setForm] = useState({ achievement_type: "", artist: "", project: "", proof_link: "" });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try { const { data } = await api.get("/achievements/types"); setTypes(data.types || []); }
      catch { /* noop */ }
    })();
    setForm({ achievement_type: "", artist: "", project: "", proof_link: "" });
    setFile(null);
  }, [open]);

  if (!open) return null;

  const onPick = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (!["image/jpeg", "image/png", "image/webp"].includes(f.type)) { toast.error("JPG, PNG, WEBP only."); return; }
    if (f.size > 50 * 1024 * 1024) { toast.error("Max 50 MB."); return; }
    setFile(f);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!file) { toast.error("Plaque image required — that's the evidence."); return; }
    if (!form.achievement_type || !form.artist.trim() || !form.project.trim()) {
      toast.error("Type, artist, and project are required."); return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("achievement_type", form.achievement_type);
      fd.append("artist", form.artist.trim());
      fd.append("project", form.project.trim());
      if (form.proof_link.trim()) fd.append("proof_link", form.proof_link.trim());
      await api.post("/achievements", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Plaque added to your Achievement Vault.");
      onSaved?.();
      onClose();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" data-testid="upload-achievement-modal" onClick={onClose}>
      <div className="bg-[#0A0A0A] border border-white/10 max-w-xl w-full max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-6 border-b border-white/5">
          <div className="flex items-center gap-2">
            <Trophy size={18} className="text-amber-500" />
            <div className="text-overline">Upload Plaque · Evidence Required</div>
          </div>
          <button onClick={onClose} className="text-neutral-400 hover:text-white"><X size={18} /></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-5">
          <div>
            <label className="text-xs uppercase tracking-widest text-neutral-500 mb-2 block">Plaque Image (required)</label>
            <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={onPick}
              className="hidden" data-testid="plaque-file-input" />
            {!file ? (
              <button type="button" onClick={() => fileRef.current?.click()} data-testid="pick-plaque-btn"
                className="w-full aspect-[4/3] border-2 border-dashed border-white/10 hover:border-amber-500/60 transition-colors flex flex-col items-center justify-center gap-2 text-neutral-500 hover:text-amber-500">
                <Upload size={28} />
                <span className="text-sm">Click to upload plaque, certificate, or screenshot</span>
                <span className="text-xs uppercase tracking-widest">JPG · PNG · WEBP · 5MB max</span>
              </button>
            ) : (
              <div className="relative aspect-[4/3] bg-[#161616] border border-amber-500/40">
                <img src={URL.createObjectURL(file)} alt="preview" className="w-full h-full object-contain" />
                <button type="button" onClick={() => setFile(null)}
                  className="absolute top-2 right-2 p-2 bg-black/70 text-white hover:bg-rose-500"><X size={14} /></button>
              </div>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-xs uppercase tracking-widest text-neutral-500 mb-2 block">Achievement Type</label>
              <select value={form.achievement_type} onChange={(e) => setForm({ ...form, achievement_type: e.target.value })}
                data-testid="achievement-type-select" required
                className="w-full bg-[#161616] border border-white/15 px-3 py-2.5 text-sm text-white outline-none focus:border-amber-500">
                <option value="" disabled>Select…</option>
                {types.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-neutral-500 mb-2 block">Artist Name</label>
              <input value={form.artist} onChange={(e) => setForm({ ...form, artist: e.target.value })}
                data-testid="achievement-artist-input" required maxLength={120}
                className="w-full bg-[#161616] border border-white/15 px-3 py-2.5 text-sm text-white outline-none focus:border-amber-500" />
            </div>
            <div className="md:col-span-2">
              <label className="text-xs uppercase tracking-widest text-neutral-500 mb-2 block">Song / Project</label>
              <input value={form.project} onChange={(e) => setForm({ ...form, project: e.target.value })}
                data-testid="achievement-project-input" required maxLength={160}
                className="w-full bg-[#161616] border border-white/15 px-3 py-2.5 text-sm text-white outline-none focus:border-amber-500" />
            </div>
            <div className="md:col-span-2">
              <label className="text-xs uppercase tracking-widest text-neutral-500 mb-2 block">Proof Link (optional)</label>
              <input value={form.proof_link} onChange={(e) => setForm({ ...form, proof_link: e.target.value })}
                placeholder="riaa.com/… · billboard.com/… · genius.com/…" data-testid="achievement-proof-input" maxLength={500}
                className="w-full bg-[#161616] border border-white/15 px-3 py-2.5 text-sm text-white outline-none focus:border-amber-500" />
            </div>
          </div>
          <div className="pt-3 flex items-center justify-end gap-3 border-t border-white/5">
            <button type="button" onClick={onClose} className="text-sm text-neutral-400 hover:text-white">Cancel</button>
            <button type="submit" disabled={busy} data-testid="submit-achievement-btn"
              className="inline-flex items-center gap-2 bg-amber-500 text-black font-semibold px-6 py-2.5 hover:brightness-110 disabled:opacity-50">
              {busy ? <><Loader2 size={14} className="animate-spin" /> Uploading…</> : <>Add to Vault</>}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function AchievementVault({ achievements: initial, isOwner = false, ownerName }) {
  const [items, setItems] = useState(initial || []);
  const [open, setOpen] = useState(false);

  useEffect(() => { if (initial !== undefined) setItems(initial); }, [initial]);

  const refresh = async () => {
    try { const { data } = await api.get("/achievements"); setItems(data.achievements || []); }
    catch { /* noop */ }
  };

  const onDelete = async (id) => {
    if (!confirm("Remove this achievement?")) return;
    try {
      await api.delete(`/achievements/${id}`);
      toast.success("Achievement removed.");
      await refresh();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const verifiedCount = items.filter((i) => i.verified).length;

  return (
    <div data-testid="achievement-vault" className="bg-[#0c0c0c] border border-white/5 p-7 lg:p-8">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Trophy size={16} className="text-amber-500" />
            <div className="text-overline">Achievement Vault<span className="text-amber-500">™</span></div>
          </div>
          <h2 className="font-display text-2xl lg:text-3xl font-medium tracking-tight">
            {isOwner ? "Your trophy room." : `${ownerName || "Producer"}'s trophy room.`}
          </h2>
          <p className="text-sm text-neutral-400 mt-2 max-w-xl">
            Evidence-only. Every plaque, every credit, every certification — tied to a real document. No flexes without proof.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right">
            <div className="font-display text-3xl text-amber-500" data-testid="vault-count">{items.length}</div>
            <div className="text-xs uppercase tracking-widest text-neutral-500">in vault</div>
          </div>
          {verifiedCount > 0 && (
            <div className="text-right">
              <div className="font-display text-3xl text-emerald-400" data-testid="vault-verified-count">{verifiedCount}</div>
              <div className="text-xs uppercase tracking-widest text-neutral-500">verified</div>
            </div>
          )}
          {isOwner && (
            <button onClick={() => setOpen(true)} data-testid="add-achievement-btn"
              className="inline-flex items-center gap-2 px-5 py-3 bg-amber-500 text-black font-semibold hover:brightness-110 transition-all">
              <Plus size={16} /> Upload Plaque
            </button>
          )}
        </div>
      </div>

      {items.length === 0 ? (
        <div className="border border-dashed border-white/10 p-12 text-center" data-testid="vault-empty">
          <Trophy size={32} className="text-neutral-700 mx-auto mb-4" />
          <div className="font-display text-lg text-white mb-2">No achievements yet.</div>
          <p className="text-sm text-neutral-500 max-w-md mx-auto">
            {isOwner ? "Upload your first plaque to start building your industry-grade trophy room." : "This producer hasn't published any verified achievements."}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {items.map((a) => (
            <AchievementCard key={a.id} achievement={a} isOwner={isOwner} onDelete={onDelete} />
          ))}
        </div>
      )}

      {isOwner && <UploadModal open={open} onClose={() => setOpen(false)} onSaved={refresh} />}
    </div>
  );
}
